import { useEffect, useRef, useState } from 'react'
import type { ProtocolGenerationPhase } from './generateInitialProtocol'

export const STEP_MICRO_LOGS: Record<ProtocolGenerationPhase, string[]> = {
  metabolics_calculated: [
    'Extracting biological profile metrics...',
    'Applying Mifflin-St Jeor base formula...',
    'Computing lean body mass coefficients...',
    'Calibrating active TDEE multipliers...',
  ],
  training_sequenced: [
    'Analyzing hypertrophy volume targets...',
    'Sequencing progressive overload steps...',
    'Structuring rest/recovery intervals...',
    'Formatting custom exercise block nodes...',
  ],
  macros_tiered: [
    'Balancing thermic effect of food factors...',
    'Isolating daily essential protein cap...',
    'Tiering performance carbohydrate thresholds...',
    'Distributing healthy dietary lipid margins...',
  ],
  budget_matched: [
    'Opening live Heisse-Preise price feed...',
    'Executing local knapsack cost optimization...',
    'Matching omnivore staples to budget limits...',
    'Assembling macro-compliant grocery list...',
  ],
  meals_sequenced: [
    'Sequencing breakfast, lunch, and dinner blocks...',
    'Balancing macro distribution across meal slots...',
    'Validating ingredient availability at local stores...',
    'Finalizing weekly nutrition matrix...',
  ],
  meal_images_ready: [
    'Rendering photorealistic meal previews...',
    'Matching plate presentation to recipe structure...',
    'Caching visuals for instant reuse...',
    'Polishing food photography assets...',
  ],
}

export const PHASE_PROGRESS: Record<ProtocolGenerationPhase, { base: number; cap: number }> = {
  metabolics_calculated: { base: 15, cap: 28 },
  training_sequenced: { base: 30, cap: 42 },
  macros_tiered: { base: 45, cap: 55 },
  meals_sequenced: { base: 58, cap: 68 },
  meal_images_ready: { base: 72, cap: 86 },
  budget_matched: { base: 90, cap: 99 },
}

const PRE_STREAM_PROGRESS = { base: 5, cap: 24 }

export function useProtocolGenerationMotion(
  isStreaming: boolean,
  generationPhase: ProtocolGenerationPhase | null,
) {
  const [microLogText, setMicroLogText] = useState('')
  const [progressPercent, setProgressPercent] = useState(PRE_STREAM_PROGRESS.base)
  const microIndexRef = useRef(0)

  const tickerPhase: ProtocolGenerationPhase | null = isStreaming
    ? (generationPhase ?? 'metabolics_calculated')
    : null

  const progressBounds = generationPhase
    ? PHASE_PROGRESS[generationPhase]
    : isStreaming
      ? PRE_STREAM_PROGRESS
      : null

  useEffect(() => {
    if (!isStreaming || !tickerPhase) {
      setMicroLogText('')
      return
    }

    const logs = STEP_MICRO_LOGS[tickerPhase]
    microIndexRef.current = 0
    setMicroLogText(logs[0] ?? '')

    const microTimer = window.setInterval(() => {
      microIndexRef.current = (microIndexRef.current + 1) % logs.length
      setMicroLogText(logs[microIndexRef.current] ?? '')
    }, 800)

    return () => clearInterval(microTimer)
  }, [isStreaming, tickerPhase])

  useEffect(() => {
    if (!isStreaming || !progressBounds) return

    setProgressPercent(progressBounds.base)

    const creepTimer = window.setInterval(() => {
      setProgressPercent((prev) => Math.min(prev + 0.5, progressBounds.cap))
    }, 300)

    return () => clearInterval(creepTimer)
  }, [isStreaming, generationPhase, progressBounds?.base, progressBounds?.cap])

  return { microLogText, progressPercent }
}
